/**
 * World Class Leader Board — RealSound soundboard.
 *
 * Every .wav is fetched and decoded once up front. The decoded buffer is used
 * for two things: playback (cached, so a click never re-decodes) and drawing a
 * real min/max waveform onto each tile's canvas. Playing a sound sweeps a
 * bright "playhead" fill across its waveform. Keyboard keys trigger tiles too.
 */

/**
 * @typedef {Object} Sound
 * @property {string} file  - basename in /audio (without extension)
 * @property {string} label - friendly display name
 * @property {string} [note]- optional sub-label / description
 */

/** Announcer voice lines — digitized speech decoded from the V*.RSH files. */
const VOICE = [
  { file: "VFAIRWY", label: "On the Fairway", note: "announcer" },
  { file: "VPUTT", label: "The Putt", note: "announcer" },
  { file: "VTRAP", label: "In the Sand Trap", note: "announcer" },
  { file: "VTREES", label: "Into the Trees", note: "announcer" },
  { file: "VCANT", label: "Tough Break", note: "announcer" },
];

/** Bird ambience — raw uncompressed samples from the "STEVE" BRDS*.EDT files. */
const BIRDS = [
  { file: "BRDS/BRDS2", label: "Bird Call", note: "chirp I" },
  { file: "BRDS/BRDS4", label: "Bird Call", note: "chirp II" },
  { file: "BRDS/BRDS5", label: "Bird Call", note: "chirp III" },
  { file: "BRDS/BRDS6", label: "Bird Call", note: "chirp IV" },
  { file: "BRDS/BRDS7", label: "Bird Call", note: "chirp V" },
];

/** Sound effects. */
const FX = [
  { file: "SWING1", label: "Swing", note: "club I" },
  { file: "SWING2", label: "Swing", note: "club II" },
  { file: "SWING3", label: "Swing", note: "club III" },
  { file: "PUTT1", label: "Putt", note: "tap I" },
  { file: "PUTT2", label: "Putt", note: "tap II" },
  { file: "RATTLE1", label: "In the Cup", note: "rattle I" },
  { file: "RATTLE2", label: "In the Cup", note: "rattle II" },
  { file: "RATTLE3", label: "In the Cup", note: "rattle III" },
  { file: "POLE", label: "Off the Pin", note: "flagpole" },
  { file: "SPLASH1", label: "Splash", note: "water I" },
  { file: "SPLASH2", label: "Splash", note: "water II" },
  { file: "TREE", label: "Off a Tree", note: "clunk" },
  { file: "SLOW", label: "Slow Roll", note: "" },
  { file: "CHEER", label: "Cheer", note: "crowd" },
  { file: "CLAPS", label: "Applause", note: "crowd" },
  { file: "OOHS", label: "Oohs", note: "crowd" },
  { file: "CRICKS", label: "Crickets", note: "ambience" },
  { file: "FROG", label: "Frog", note: "ambience" },
];

/** Waveform render resolution. Low + pixelated on purpose, to match the art. */
const WAVE_W = 96;
const WAVE_H = 40;

/** Per-group waveform colors: [dim (unplayed), bright (played)]. */
const WAVE_COLORS = {
  voice: ["rgba(91,110,225,0.45)", "#9aa8ff"],
  fx: ["rgba(55,200,55,0.42)", "#7dffa5"],
  birds: ["rgba(255,207,63,0.4)", "#ffe38a"],
};

/**
 * Reduce a decoded AudioBuffer to `buckets` [min, max] pairs in -1..1.
 * @param {AudioBuffer} buffer
 * @param {number} buckets
 * @returns {[number, number][]}
 */
function computePeaks(buffer, buckets) {
  const chans = buffer.numberOfChannels;
  const len = buffer.length;
  const data = [];
  for (let c = 0; c < chans; c++) data.push(buffer.getChannelData(c));

  const peaks = new Array(buckets);
  const per = len / buckets;
  for (let i = 0; i < buckets; i++) {
    const start = Math.floor(i * per);
    const end = Math.min(len, Math.floor((i + 1) * per));
    let min = 1;
    let max = -1;
    for (let j = start; j < end; j++) {
      // Average channels to mono (our WAVs are already mono).
      let v = 0;
      for (let c = 0; c < chans; c++) v += data[c][j];
      v /= chans;
      if (v < min) min = v;
      if (v > max) max = v;
    }
    peaks[i] = end > start ? [min, max] : [0, 0];
  }
  return peaks;
}

/**
 * Measure a decoded clip: duration, sample rate, and peak / RMS levels.
 * @param {AudioBuffer} buffer
 * @returns {{duration:number, sampleRate:number, samples:number,
 *            peak:number, rms:number, peakDb:number, rmsDb:number}}
 */
function analyze(buffer) {
  const ch = buffer.getChannelData(0);
  let peak = 0;
  let sumSq = 0;
  for (let i = 0; i < ch.length; i++) {
    const a = Math.abs(ch[i]);
    if (a > peak) peak = a;
    sumSq += ch[i] * ch[i];
  }
  const rms = ch.length ? Math.sqrt(sumSq / ch.length) : 0;
  const dB = (x) => (x > 0 ? 20 * Math.log10(x) : -Infinity);
  return {
    duration: buffer.duration,
    sampleRate: buffer.sampleRate,
    samples: buffer.length,
    peak,
    rms,
    peakDb: dB(peak),
    rmsDb: dB(rms),
  };
}

/**
 * Human-readable dB, e.g. "-3.2 dB" or "-inf dB".
 * @param {number} db
 */
function fmtDb(db) {
  return Number.isFinite(db) ? `${db.toFixed(1)} dB` : "-∞ dB";
}

/**
 * Draw a mirrored min/max waveform. Buckets left of the playhead use the bright
 * color; the rest use the dim color.
 * @param {CanvasRenderingContext2D} ctx
 * @param {[number, number][]} peaks
 * @param {[string, string]} colors  [dim, bright]
 * @param {number} progress          0..1 playhead position
 */
function drawWave(ctx, peaks, colors, progress) {
  const [dim, bright] = colors;
  const center = WAVE_H / 2;
  const half = WAVE_H / 2 - 1;
  const playedTo = progress * peaks.length;

  ctx.clearRect(0, 0, WAVE_W, WAVE_H);
  for (let i = 0; i < peaks.length; i++) {
    const [mn, mx] = peaks[i];
    const yTop = center - mx * half;
    const h = Math.max(1, center - mn * half - yTop);
    ctx.fillStyle = i < playedTo ? bright : dim;
    ctx.fillRect(i, yTop, 1, h);
  }
}

/**
 * Render the soundboard tiles from a manifest into a container. Synchronous, so
 * the tiles exist before SoundBoard queries for them.
 * @param {string} selector - CSS selector for the target `.buttons` element
 * @param {Sound[]} sounds
 */
function renderBoard(selector, sounds) {
  const container = document.querySelector(selector);
  if (!container) return;
  const group = container.dataset.group || "fx";
  container.innerHTML = sounds
    .map(
      (s) => `
      <button class="button button--${group}" data-audio="/audio/${s.file}.wav" data-group="${group}" type="button">
        <canvas class="button__wave" width="${WAVE_W}" height="${WAVE_H}" aria-hidden="true"></canvas>
        <span class="button__dur" aria-hidden="true"></span>
        <span class="button__label">${s.label}</span>
        ${s.note ? `<span class="button__note">${s.note}</span>` : ""}
        <span class="button__file">${s.file.split("/").pop()}.wav</span>
      </button>`
    )
    .join("");
}

/**
 * SoundBoard manages audio loading, playback, waveforms and keyboard controls.
 */
class SoundBoard {
  constructor() {
    /** @type {AudioContext|null} */
    this.audioContext = null;
    /** @type {Map<string, AudioBuffer>} decoded, keyed by audio url */
    this.buffers = new Map();
    /** @type {NodeListOf<HTMLButtonElement>} */
    this.buttons = document.querySelectorAll(".button");
    /** @type {Map<Element, {source: AudioBufferSourceNode, raf: number}>} */
    this.active = new Map();
    /** @type {Map<string, Element>} */
    this.keyMap = new Map();

    this.attackTime = 0.02;
    this.decayTime = 0.06;

    this.ensureContext();
    this.setupEventListeners();
    this.setupKeyboardControls();
    this.loadAll();
  }

  /** Create the AudioContext (starts suspended until the first gesture). */
  ensureContext() {
    if (this.audioContext) return;
    const Ctor = window.AudioContext || window.webkitAudioContext;
    this.audioContext = new Ctor();
  }

  /** Fetch + decode every tile's wav, then draw its waveform. */
  async loadAll() {
    await Promise.all(
      Array.from(this.buttons).map((button) => this.loadButton(button))
    );
  }

  /** @param {HTMLButtonElement} button */
  async loadButton(button) {
    const url = button.dataset.audio;
    if (!url) return;

    const canvas = button.querySelector(".button__wave");
    const ctx = canvas ? canvas.getContext("2d") : null;
    const colors = WAVE_COLORS[button.dataset.group] || WAVE_COLORS.fx;

    try {
      const res = await fetch(url);
      const encoded = await res.arrayBuffer();
      const buffer = await this.audioContext.decodeAudioData(encoded);
      this.buffers.set(url, buffer);

      if (ctx) {
        const peaks = computePeaks(buffer, WAVE_W);
        button._wave = { ctx, peaks, colors };
        drawWave(ctx, peaks, colors, 0);
      }

      // Per-file waveform analysis: duration badge + full stats on hover.
      const stats = analyze(buffer);
      const name = url.split("/").pop();
      const dur = button.querySelector(".button__dur");
      if (dur) dur.textContent = `${stats.duration.toFixed(2)}s`;
      button.title =
        `${name} — ${stats.duration.toFixed(2)}s · ${stats.sampleRate} Hz · ` +
        `${stats.samples.toLocaleString()} samples · ` +
        `peak ${fmtDb(stats.peakDb)} · RMS ${fmtDb(stats.rmsDb)}`;
    } catch (e) {
      console.error("Failed to load", url, e);
    }
  }

  setupEventListeners() {
    this.buttons.forEach((button) => {
      button.addEventListener("click", () => this.playSound(button));
    });
  }

  /** @param {HTMLButtonElement} button */
  async playSound(button) {
    const url = button.dataset.audio;
    const buffer = this.buffers.get(url);
    if (!buffer) return; // not decoded yet

    try {
      this.ensureContext();
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      this.stop(button); // restart cleanly if already playing

      const source = this.audioContext.createBufferSource();
      const gain = this.audioContext.createGain();
      source.buffer = buffer;
      source.connect(gain);
      gain.connect(this.audioContext.destination);

      const t0 = this.audioContext.currentTime;
      const duration = buffer.duration;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(1, t0 + this.attackTime);
      const decayStart = Math.max(t0, t0 + duration - this.decayTime);
      gain.gain.setValueAtTime(1, decayStart);
      gain.gain.linearRampToValueAtTime(0, decayStart + this.decayTime);

      source.start(0);
      button.classList.add("playing");

      const wave = button._wave;
      const tick = () => {
        const entry = this.active.get(button);
        if (!entry) return;
        const progress = Math.min(1, (this.audioContext.currentTime - t0) / duration);
        if (wave) drawWave(wave.ctx, wave.peaks, wave.colors, progress);
        if (progress < 1) entry.raf = requestAnimationFrame(tick);
      };

      source.onended = () => this.stop(button);

      const entry = { source, raf: requestAnimationFrame(tick) };
      this.active.set(button, entry);
    } catch (error) {
      console.error("Error playing sound:", error);
    }
  }

  /** Stop playback (if any) and reset the tile's waveform. */
  stop(button) {
    const entry = this.active.get(button);
    if (!entry) return;
    this.active.delete(button);
    cancelAnimationFrame(entry.raf);
    entry.source.onended = null;
    try {
      entry.source.stop(0);
    } catch (_) {}
    button.classList.remove("playing");
    const wave = button._wave;
    if (wave) drawWave(wave.ctx, wave.peaks, wave.colors, 0);
  }

  setupKeyboardControls() {
    const keys = "1234567890QWERTYUIOPASDFGHJKLZXCVBNM".split("");
    this.buttons.forEach((button, index) => {
      if (index >= keys.length) return;
      const key = keys[index];
      this.keyMap.set(key.toLowerCase(), button);
      const indicator = document.createElement("div");
      indicator.className = "key-indicator";
      indicator.textContent = key;
      button.appendChild(indicator);
    });

    document.addEventListener("keydown", (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      const button = this.keyMap.get(e.key.toLowerCase());
      if (!button) return;
      this.playSound(button);
      button.classList.add("key-pressed");
      setTimeout(() => button.classList.remove("key-pressed"), 180);
    });
  }
}

/**
 * On phones the hardware silent/ring switch (iOS especially) mutes Web Audio,
 * so warn the user once. Dismissal is remembered.
 */
function setupMobileNotice() {
  const el = document.querySelector(".mobile-notice");
  if (!el) return;

  const coarse = window.matchMedia?.("(pointer: coarse)").matches;
  const ua = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!coarse && !ua) return;

  try {
    if (localStorage.getItem("wclb-mute-notice") === "1") return;
  } catch (_) {}

  el.hidden = false;
  el.querySelector(".mobile-notice__close")?.addEventListener("click", () => {
    el.hidden = true;
    try {
      localStorage.setItem("wclb-mute-notice", "1");
    } catch (_) {}
  });
}

document.addEventListener("DOMContentLoaded", () => {
  renderBoard('.buttons[data-group="voice"]', VOICE);
  renderBoard('.buttons[data-group="fx"]', FX);
  renderBoard('.buttons[data-group="birds"]', BIRDS);
  new SoundBoard();

  setupMobileNotice();
});
